// ─── Import bancaire : analyse et application (Firestore) ─────────
// Porté de MoPCal. Rattachement par ESPACE (cf. MODELE-DONNEES.md).
//
// Rien n'est jamais appliqué d'office : l'analyse produit un PREMIER FILTRE
// (déjà importé / doublon probable / nouveau) que l'utilisateur valide ligne à
// ligne — Rapprocher, Importer, ou Ignorer.
import { db } from '@mop/core';
import {
  collection, query, where, getDocs, setDoc, doc, updateDoc, writeBatch, onSnapshot,
} from 'firebase/firestore';
import { motifDeLibelle, hashCourt } from './releveBancaire';
import { occurrencesDuMois, moisDeDate } from './occurrences';
import { meilleureLigne } from './rapprochementLignes';

// ── Règles apprises (réconciliation) ─────────────────────────────
// Quand l'utilisateur confirme un rapprochement, on mémorise : motif du libellé
// banque → poste + catégorie. Les imports suivants s'en servent pour SUGGÉRER un
// rangement, jamais pour l'appliquer sans validation.
export async function enregistrerRegleImport({ espaceId, libelle, budgetId, categorie, recurrenceId, ligneId, user }) {
  const motif = motifDeLibelle(libelle);
  if (!espaceId || !motif || !budgetId || categorie === 'import') return;
  await setDoc(doc(db, 'import_regles', `regle_${espaceId}_${hashCourt(motif)}`), {
    espaceId, motif, budgetId,
    categorie: categorie || 'autre',
    recurrenceId: recurrenceId || null,
    // Ligne PREVUE apprise : « EDF » ne ressemble pas a « Electricite », aucun
    // rapprochement automatique ne les reliera jamais. Une fois le lien fait a
    // la main, on s'en souvient — c'est ce qui rend l'import silencieux au
    // bout de deux ou trois releves.
    ligneId: ligneId || null,
    libelleExemple: String(libelle || '').trim(),
    maj: new Date().toISOString(),
    creePar: user?.uid || null,
  }, { merge: true });
}

export function ecouterReglesImport(espaceId, callback) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'import_regles'), where('espaceId', '==', espaceId));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => callback([]));
}

// ── Le poste système « Import » n'existe plus (10/08) ────────────
// `trouverOuCreerBudgetImport` créait un poste de transit `import_<espaceId>`
// qui recevait toute opération sans enveloppe reconnue. Supprimé sur décision
// de Cédric : ce n'était pas une enveloppe mais un déversoir, et il s'affichait
// dans la liste des postes avec la somme de tous les débits du relevé.
// Les opérations importées vivent désormais dans leur COMPTE (onglet Comptes,
// 0.6.4.0) et naissent sans `budgetId`.
// Nettoyage des données existantes : `scripts/retirer-budget-import.js`.

const cents = (m) => Math.round(Math.abs(m || 0) * 100);
const moisDe = (d) => String(d || '').slice(0, 7);
const cleDe = (d, signe, m) => `${moisDe(d)}|${signe}|${cents(m)}`;

// Référence d'import STOCKÉE. Elle est préfixée par le compte, car une
// référence CSV vaut `csv:date|montant|libellé` : sans le compte, deux relevés
// de comptes DIFFÉRENTS portant une opération identique produiraient la même
// référence, et la seconde serait refusée comme un doublon. (Le FITID d'un OFX
// est unique par banque, mais on applique la même règle pour tous.)
export const refStockee = (compteId, ref) => (compteId ? `${compteId}:${ref}` : ref);

// ── Analyse (avant import) ───────────────────────────────────────
// Statut par opération lue : « deja » (même référence, ou même date+sens+montant
// qu'une opération déjà importée dans un autre format), « possible » (une
// opération existante a le même MOIS + SENS + MONTANT), sinon « nouveau ».
//
// Le LIBELLÉ est volontairement IGNORÉ dans la clé de rapprochement : le relevé
// a un libellé brut (« PRET IMMOBILIER ECH 08/06/26 ») très différent d'une
// saisie manuelle propre (« Prêt - B113 »). On veut quand même les rapprocher
// pour éviter un double comptage.
export async function analyserOperations({ espaceId, operations, compteId = null }) {
  const par = (col) => getDocs(query(collection(db, col), where('espaceId', '==', espaceId)));
  const [dep, bud, reg, rec, lig, exc] = await Promise.all([
    par('depenses'), par('budgets'), par('import_regles'), par('recurrences_budget'),
    par('lignesBudget'), par('occurrencesBudget'),
  ]);

  const nomBudget = {};
  bud.forEach(b => { nomBudget[b.id] = b.data().nom || ''; });
  const recIds = new Set(); rec.forEach(r => recIds.add(r.id));

  // Règle retenue seulement si son poste existe encore et n'est pas le transit ;
  // le lien vers une récurrente supprimée est écarté (sinon lien orphelin).
  const reglesParMotif = {};
  reg.forEach(r => {
    const x = r.data();
    if (x.motif && x.budgetId && nomBudget[x.budgetId] && nomBudget[x.budgetId] !== 'Import') {
      reglesParMotif[x.motif] = {
        budgetId: x.budgetId, categorie: x.categorie || 'autre',
        recurrenceId: (x.recurrenceId && recIds.has(x.recurrenceId)) ? x.recurrenceId : null,
        ligneId: x.ligneId || null,     // valide plus bas : la ligne peut avoir ete supprimee
        budgetNom: nomBudget[x.budgetId],
      };
    }
  });

  const refs = new Set();
  // Dédup inter-format (OFX ↔ CSV) : `date|sens|montant` des opérations DÉJÀ
  // importées, mais RANGÉES PAR COMPTE — deux comptes peuvent légitimement
  // recevoir la même opération le même jour (deux salaires, un loyer perçu deux
  // fois…). Sans cette distinction, la seconde serait marquée « déjà importée »
  // à tort. Les opérations sans compte (imports d'avant les comptes) restent
  // comparables à tout, faute de mieux.
  const importesParCompte = new Map();   // compteId | '' → Set(clés)
  const ajouterCle = (cpt, cle) => {
    const k = cpt || '';
    if (!importesParCompte.has(k)) importesParCompte.set(k, new Set());
    importesParCompte.get(k).add(cle);
  };
  const parCle = {};
  dep.forEach(d => {
    const x = d.data();
    if (x.importRef) {
      refs.add(x.importRef);
      ajouterCle(x.compteId, `${x.date}|${x.sens === 'revenu' ? 'R' : 'D'}|${cents(x.montant)}`);
    }
    // Cibles = de VRAIES opérations non encore rapprochées. On exclut ce qui
    // vient d'un import (rapprocher un import avec un import n'a aucun sens) et
    // ce qui est déjà rapproché (ne pas écraser un rapprochement existant).
    // Le critère est la CATÉGORIE, posée par le code : le test sur le nom du
    // poste (« Import ») est tombé avec le poste de transit le 10/08 — et il
    // aurait de toute façon exclu à tort une enveloppe que l'utilisateur aurait
    // nommée « Import » lui-même.
    if (x.categorie === 'import' || x.importRef) return;
    const signe = x.sens === 'revenu' ? 'R' : 'D';
    const cand = {
      id: d.id, libelle: x.libelle || '', date: x.date,
      montant: (signe === 'R' ? 1 : -1) * Math.abs(x.montant || 0),
      budgetId: x.budgetId || null, categorie: x.categorie || 'autre',
      recurrenceId: x.recurrenceId || null, budget: nomBudget[x.budgetId] || '—',
    };
    // Un remboursement est un TRANSFERT : il apparaît au débit chez l'un et au
    // crédit chez l'autre → candidat des DEUX côtés.
    const cles = x.type === 'remboursement'
      ? [cleDe(x.date, 'D', x.montant), cleDe(x.date, 'R', x.montant)]
      : [cleDe(x.date, signe, x.montant)];
    for (const cle of cles) (parCle[cle] = parCle[cle] || []).push(cand);
  });

  // ── Cibles PREVUES ──────────────────────────────────────────────
  // Les occurrences ne sont pas stockees : on les calcule, mois par mois, sur
  // les seuls mois couverts par le releve (occurrencesDuMois fait deja le
  // travail — statuts, montants ajustes, inflation).
  const lignes = lig.docs.map(d => ({ id: d.id, ...d.data() }));
  const ligneIds = new Set(lignes.map(l => l.id));
  const exceptions = exc.docs.map(d => ({ id: d.id, ...d.data() }));
  const actuals = dep.docs.map(d => ({ id: d.id, ...d.data() }));
  const aujDK = new Date().toISOString().slice(0, 10);
  const occParMois = new Map();
  const occurrencesDe = (mk) => {
    if (!mk) return [];
    if (!occParMois.has(mk)) {
      occParMois.set(mk, occurrencesDuMois({ lignes, actuals, exceptions, mk, auj: aujDK }));
    }
    return occParMois.get(mk);
  };

  const dejaVu = (cle) => {
    // Même compte : doublon certain. Opérations sans compte : on ne peut pas
    // trancher, on prévient quand même (l'utilisateur garde la main).
    const duCompte = importesParCompte.get(compteId || '');
    if (duCompte && duCompte.has(cle)) return true;
    const sansCompte = importesParCompte.get('');
    return !!(compteId && sansCompte && sansCompte.has(cle));
  };

  // Ligne prevue visee par une operation : la regle apprise fait FOI (elle vient
  // d'un choix explicite), sinon on cherche la meilleure correspondance.
  const ligneVisee = (o, regle) => {
    const mk = moisDeDate(o.date);
    const occs = occurrencesDe(mk);
    if (regle?.ligneId && ligneIds.has(regle.ligneId)) {
      const occ = occs.find(x => x.ligneId === regle.ligneId);
      if (occ && (occ.statut === 'manquante' || occ.statut === 'partielle')) {
        return {
          ligneId: occ.ligneId, libelle: occ.ligne.libelle || '',
          budgetId: occ.ligne.budgetId || null, categorie: occ.ligne.categorie || 'autre',
          mois: occ.mois, prevu: occ.prevu,
          ecart: Math.round((Math.abs(o.montant || 0) - occ.prevu) * 100) / 100,
          score: 1000, apprise: true,
        };
      }
    }
    return meilleureLigne(o, occs);
  };

  return operations.map(o => {
    const regle = reglesParMotif[motifDeLibelle(o.libelle)] || null;
    if (refs.has(refStockee(compteId, o.ref))) return { ...o, statut: 'deja', match: null, matchCount: 0, regle, matchLigne: null };
    const signe = (o.montant || 0) >= 0 ? 'R' : 'D';
    if (dejaVu(`${o.date}|${signe}|${cents(o.montant)}`)) return { ...o, statut: 'deja', match: null, matchCount: 0, regle, matchLigne: null };
    const cands = parCle[cleDe(o.date, signe, o.montant)] || [];
    // Une operation DEJA SAISIE prime sur une ligne prevue : la rattacher aussi
    // compterait la meme sortie d'argent deux fois.
    const matchLigne = cands.length ? null : ligneVisee(o, regle);
    return {
      ...o, statut: cands.length ? 'possible' : 'nouveau',
      match: cands[0] || null, matchCount: cands.length, regle, matchLigne,
    };
  });
}

// ── Application (rapprochements + rattachements + créations) ─────
// rapprochements : enrichit l'opération EXISTANTE des infos banque, aligne sa
//   date de paiement sur la date banque, la passe en « payé », APPREND la règle.
// rattachements : CRÉE l'opération en la posant sur une ligne PRÉVUE (`ligneId`
//   + `moisImputation`) — l'occurrence du mois passe de « manquante » à
//   « réalisée » sans passer par le transit ni par un rattachement manuel.
// creations : crée l'opération dans le poste appris, sinon dans le transit.
// Dédoublonnage de sécurité par `importRef`.
export async function appliquerImport({ espaceId, user, compteId = null, rapprochements = [], rattachements = [], creations = [] }) {
  const par = (col) => getDocs(query(collection(db, col), where('espaceId', '==', espaceId)));
  const [depSnap, budSnap] = await Promise.all([par('depenses'), par('budgets')]);

  const dejaLa = new Set();
  depSnap.forEach(d => { const r = d.data().importRef; if (r) dejaLa.add(r); });
  const budgetsValides = new Set(); budSnap.forEach(b => budgetsValides.add(b.id));

  const nom = user?.prenom || user?.email || '';
  let rapproches = 0, rattaches = 0, crees = 0, ignores = 0;

  for (const r of rapprochements) {
    const o = r?.op;
    if (!r?.depenseId || !o || dejaLa.has(refStockee(compteId, o.ref))) { ignores++; continue; }
    await updateDoc(doc(db, 'depenses', r.depenseId), {
      importRef: refStockee(compteId, o.ref), libelleBanque: o.libelle || '',
      dateBanque: o.date, datePaiement: o.date, paye: true,
      // Le compte est une info du RELEVÉ : on la pose aussi sur une opération
      // saisie à la main qu'on vient de rapprocher — elle vient bien de là.
      ...(compteId ? { compteId } : {}),
    });
    dejaLa.add(refStockee(compteId, o.ref));
    rapproches++;
    await enregistrerRegleImport({ espaceId, libelle: o.libelle, budgetId: r.budgetId, categorie: r.categorie, recurrenceId: r.recurrenceId, user });
  }

  // Rattachements : même forme que les créations, mais la cible est connue.
  // On garde le `budgetId` de la LIGNE (pas le transit) et on fige le mois
  // d'imputation sur celui de l'occurrence visée — sinon une opération payée le
  // 2 septembre pour l'échéance d'août compterait dans le mauvais mois.
  const aRattacher = [];
  for (const r of rattachements) {
    const o = r?.op;
    if (!o || !r?.ligneId || dejaLa.has(refStockee(compteId, o.ref))) { ignores++; continue; }
    dejaLa.add(refStockee(compteId, o.ref));
    aRattacher.push(r);
  }

  const aCreer = [];
  for (const c of (creations || [])) {
    if (!c?.op || dejaLa.has(refStockee(compteId, c.op.ref))) continue;
    dejaLa.add(refStockee(compteId, c.op.ref));
    let budgetId = c.budgetId, categorie = c.categorie || 'import', recurrenceId = c.recurrenceId || null;
    // Sans enveloppe reconnue, l'opération naît SANS enveloppe — et non dans un
    // poste de transit « Import » (supprimé le 10/08, décision de Cédric).
    // Motif : un relevé décrit des mouvements de COMPTE. Le poste de transit
    // s'affichait comme une enveloppe parmi les autres et écrasait la liste
    // (80 085,50 € — la somme des débits, présentée comme un budget). Le compte
    // porte désormais ces opérations, l'écran Comptes les montre : elles ont un
    // point d'entrée sans avoir besoin d'un faux poste.
    if (!budgetId || !budgetsValides.has(budgetId)) {
      budgetId = null; categorie = 'import'; recurrenceId = null;
    }
    aCreer.push({ op: c.op, budgetId, categorie, recurrenceId });
  }
  for (let i = 0; i < aRattacher.length; i += 450) {
    const batch = writeBatch(db);
    for (const r of aRattacher.slice(i, i + 450)) {
      const o = r.op;
      batch.set(doc(collection(db, 'depenses')), {
        espaceId,
        budgetId: (r.budgetId && budgetsValides.has(r.budgetId)) ? r.budgetId : null,
        ligneId: r.ligneId, moisImputation: r.mois || null,
        recurrenceId: null, paye: true, compteId: compteId || null,
        sens: o.montant < 0 ? 'depense' : 'revenu',
        montant: Math.round(Math.abs(o.montant) * 100) / 100,
        payeurUid: user?.uid || null, payeurNom: nom,
        categorie: r.categorie || 'autre', libelle: o.libelle || '', date: o.date,
        datePaiement: o.date, typePaiement: '', commentaire: '', pieces: [],
        importRef: refStockee(compteId, o.ref), source: 'import',
        libelleBanque: o.libelle || '', dateBanque: o.date,
        creePar: user?.uid || null, creeLe: new Date().toISOString(),
      });
      rattaches++;
    }
    await batch.commit();
  }
  // La règle s'apprend APRÈS l'écriture : on ne mémorise que ce qui a abouti.
  for (const r of aRattacher) {
    await enregistrerRegleImport({
      espaceId, libelle: r.op.libelle, budgetId: r.budgetId,
      categorie: r.categorie, ligneId: r.ligneId, user,
    });
  }

  for (let i = 0; i < aCreer.length; i += 450) {
    const batch = writeBatch(db);
    for (const c of aCreer.slice(i, i + 450)) {
      const o = c.op;
      batch.set(doc(collection(db, 'depenses')), {
        espaceId, budgetId: c.budgetId, recurrenceId: c.recurrenceId, paye: true,
        compteId: compteId || null,
        sens: o.montant < 0 ? 'depense' : 'revenu',
        montant: Math.round(Math.abs(o.montant) * 100) / 100,
        payeurUid: user?.uid || null, payeurNom: nom,
        categorie: c.categorie, libelle: o.libelle || '', date: o.date,
        datePaiement: o.date, typePaiement: '', commentaire: '', pieces: [],
        importRef: refStockee(compteId, o.ref), source: 'import',
        libelleBanque: o.libelle || '', dateBanque: o.date,
        creePar: user?.uid || null, creeLe: new Date().toISOString(),
      });
      crees++;
    }
    await batch.commit();
  }

  return { rapproches, rattaches, crees, ignores };
}
